import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import StarsIcon from "../../assets/sparkling.png";
import { USER_TYPE } from "../../constants/common"; // Assuming this exists
import lessonServices from "../../services/lessonServices";
import { useNavigateWithLang } from "../../utils/navigation"; // Assuming this exists
import PuzzleGameFinishModal from "../modals/common/PuzzleGameFinishModal";
import PuzzleGameStartModal from "../modals/common/PuzzleGameStartModal";

const GRID_SIZE = 3;
const TILE_SIZE = 130;
const CUSTOM_SHUFFLE_ORDER = [6, 3, 7, 1, 8, 0, 4, 2, 5]; // any permutation of [0..8]

const PuzzleGame = ({ gameDetails }) => {
  const { id: lessonID } = useParams();
  const { t } = useTranslation();
  const [userGameId, setUserGameId] = useState(null);
  const [gameImage, setGameImage] = useState("");
  const [tiles, setTiles] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);
  const [numberOfMoves, setNumberOfMoves] = useState(0);
  const puzzleGameStartModalRef = useRef();
  const puzzleGameFinishModalRef = useRef();
  const navigate = useNavigateWithLang();

  const initializeGame = async () => {
    const data = await lessonServices.getLessonGameStage(gameDetails?.id);
    setUserGameId(data?.user_game_id);
    setGameImage(encodeURI(data?.card?.image));
    setTiles(CUSTOM_SHUFFLE_ORDER);
    setNumberOfMoves(0);
  };

  const handleDragStart = (index) => {
    setDragIndex(index);
  };

  const handleDrop = (dropIndex) => {
    const newTiles = [...tiles];
    [newTiles[dragIndex], newTiles[dropIndex]] = [newTiles[dropIndex], newTiles[dragIndex]];
    setTiles(newTiles);
    setDragIndex(null);
    setNumberOfMoves((prev) => prev + 1);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const isSolved = tiles.every((tile, idx) => tile === idx);

  useEffect(() => {
    puzzleGameStartModalRef.current?.openModal();
  }, []);

  const handleGameCompletion = async () => {
    try {
      await lessonServices.submitGame(userGameId, {
        number_of_moves: numberOfMoves,
        is_completed: true,
      });
      await lessonServices.finishLesson(lessonID);
      navigate(`/${USER_TYPE.STUDENT.ROLE}/learning-area`);
    } catch (err) {
      console.error("Failed to submit game:", err);
    }
  };

  return (
    <div className="ltr-ignore">
      <div className="m-auto mb-4 flex max-w-[52rem] items-center justify-center rounded-3xl bg-blue-500 p-4 shadow-lg ring-4 ring-blue-300">
        <div className="flex items-center gap-2">
          <img className="w-11" src={StarsIcon} alt="" />
          <h2 className="text-3xl font-semibold text-white">{t("puzzle_game_title")}</h2>
          <img className="w-11" src={StarsIcon} alt="" />
        </div>
      </div>

      <div className="flex flex-col items-center space-y-2">
        <div className="flex gap-6">
          {/* Original Image Reference */}
          <div
            className="rounded-3xl border-4 border-blue-500"
            style={{
              width: (TILE_SIZE + 4) * GRID_SIZE,
              height: (TILE_SIZE + 4) * GRID_SIZE,
              backgroundImage: `url(${gameImage})`,
              backgroundSize: `${(TILE_SIZE + 2) * GRID_SIZE}px ${(TILE_SIZE + 2) * GRID_SIZE}px`,
            }}
          />

          {/* Puzzle Grid */}
          <div
            className="grid overflow-hidden rounded-3xl border-4 border-blue-500 bg-blue-500"
            style={{
              gridTemplateColumns: `repeat(${GRID_SIZE}, ${TILE_SIZE}px)`,
              gridTemplateRows: `repeat(${GRID_SIZE}, ${TILE_SIZE}px)`,
              display: "grid",
              gap: "2px",
            }}
          >
            {tiles.map((tileIndex, i) => (
              <div
                key={i}
                draggable
                onDragStart={() => handleDragStart(i)}
                onDragOver={handleDragOver}
                onDrop={() => handleDrop(i)}
                className="border border-gray-200"
                style={{
                  width: `${TILE_SIZE}px`,
                  height: `${TILE_SIZE}px`,
                  backgroundImage: `url(${gameImage})`,
                  backgroundPosition: `-${(tileIndex % GRID_SIZE) * TILE_SIZE}px -${
                    Math.floor(tileIndex / GRID_SIZE) * TILE_SIZE
                  }px`,
                  backgroundSize: `${TILE_SIZE * GRID_SIZE}px`,
                  backgroundColor: tileIndex === tiles.length - 1 ? "#f3f4f6" : "transparent",
                  cursor: "grab",
                }}
              />
            ))}
          </div>
        </div>

        {/* Submit Button */}
        <button
          onClick={() => {
            puzzleGameFinishModalRef.current?.openModal();
          }}
          className="wavy-btn relative mt-1 w-1/2 rounded-3xl border-4 border-[#ffe4af] bg-orange-500 p-4 text-lg font-semibold text-white transition duration-200"
        >
          {t("submit")}
        </button>

        {/* Modals */}
        <PuzzleGameStartModal ref={puzzleGameStartModalRef} onSubmit={initializeGame} />
        <PuzzleGameFinishModal
          ref={puzzleGameFinishModalRef}
          onSubmit={handleGameCompletion}
          onSubmitReset={initializeGame}
          status={isSolved ? "correct" : "wrong"}
        />
      </div>
    </div>
  );
};

export default PuzzleGame;
